import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, AlertCircle, X } from 'lucide-react';
import { useActiveWorkflows } from '../context/ActiveWorkflowsContext';

const TOAST_TTL_MS = 6000;

function toastConfig(event) {
  const id = event.workflow.workflow_id;
  const rawLabel = event.workflow.task_description || event.workflow.task_key || 'Workflow';
  // Toast title needs to fit on one line — truncate long task descriptions.
  const label = rawLabel.length > 40 ? `${rawLabel.slice(0, 40).trimEnd()}…` : rawLabel;
  switch (event.type) {
    case 'completed':
      return {
        id: `${id}:completed`,
        workflowId: id,
        title: `${label} completed`,
        body: 'View results in the workflow history.',
        kind: 'success',
        action: 'view-history',
      };
    case 'failed':
      return {
        id: `${id}:failed`,
        workflowId: id,
        title: `${label} failed`,
        body: 'Open the flow to inspect the error.',
        kind: 'error',
        action: 'view-flow',
      };
    case 'needs_input':
      return {
        id: `${id}:needs_input`,
        workflowId: id,
        title: `${label} needs input`,
        body: 'A node is waiting for your approval.',
        kind: 'warn',
        action: 'view-flow',
      };
    default:
      return null;
  }
}

const KIND_STYLES = {
  success: {
    icon: CheckCircle,
    border: 'border-green-200',
    bg: 'bg-green-50',
    iconColor: 'text-green-600',
    titleColor: 'text-green-900',
    bodyColor: 'text-green-800',
  },
  error: {
    icon: XCircle,
    border: 'border-red-200',
    bg: 'bg-red-50',
    iconColor: 'text-red-600',
    titleColor: 'text-red-900',
    bodyColor: 'text-red-800',
  },
  warn: {
    icon: AlertCircle,
    border: 'border-amber-200',
    bg: 'bg-amber-50',
    iconColor: 'text-amber-600',
    titleColor: 'text-amber-900',
    bodyColor: 'text-amber-800',
  },
};

export default function ToastHost() {
  const { subscribe, acknowledge } = useActiveWorkflows();
  const navigate = useNavigate();
  const [toasts, setToasts] = useState([]);

  useEffect(() => {
    const unsub = subscribe((events) => {
      const incoming = events.map(toastConfig).filter(Boolean);
      if (!incoming.length) return;
      setToasts((curr) => {
        // Dedup by id so onfocus replays don't stack identical toasts.
        const seen = new Set(curr.map((t) => t.id));
        return [...curr, ...incoming.filter((t) => !seen.has(t.id))];
      });
    });
    return unsub;
  }, [subscribe]);

  useEffect(() => {
    if (!toasts.length) return undefined;
    const timers = toasts.map((t) =>
      setTimeout(() => {
        setToasts((curr) => curr.filter((x) => x.id !== t.id));
      }, TOAST_TTL_MS)
    );
    return () => timers.forEach(clearTimeout);
  }, [toasts]);

  const handleAction = (toast) => {
    if (toast.action === 'view-history') {
      acknowledge(toast.workflowId);
      navigate(`/runs/open?workflow=${toast.workflowId}&view=history`);
    } else {
      navigate(`/runs/open?workflow=${toast.workflowId}&view=visualization`);
    }
    setToasts((curr) => curr.filter((x) => x.id !== toast.id));
  };

  const handleDismiss = (toast) => {
    setToasts((curr) => curr.filter((x) => x.id !== toast.id));
  };

  if (!toasts.length) return null;

  return (
    <div className="fixed top-4 right-4 z-[60] flex flex-col gap-2 max-w-sm">
      {toasts.map((t) => {
        const style = KIND_STYLES[t.kind];
        const Icon = style.icon;
        return (
          <div
            key={t.id}
            className={`${style.bg} ${style.border} border rounded-lg shadow-lg p-3 flex items-start gap-3`}
          >
            <Icon className={`${style.iconColor} w-5 h-5 flex-shrink-0 mt-0.5`} />
            <div className="flex-1 min-w-0">
              <p className={`${style.titleColor} text-sm font-semibold`}>{t.title}</p>
              <p className={`${style.bodyColor} text-xs mt-0.5`}>{t.body}</p>
              <button
                onClick={() => handleAction(t)}
                className={`${style.titleColor} text-xs font-medium underline mt-1.5 hover:opacity-80`}
              >
                {t.action === 'view-history' ? 'View results' : 'Open flow'}
              </button>
            </div>
            <button
              onClick={() => handleDismiss(t)}
              className="text-gray-400 hover:text-gray-600 flex-shrink-0"
              aria-label="Dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        );
      })}
    </div>
  );
}
